import { useCallback, useEffect, useState } from "react";
import { Building2, KeyRound, Save, UserRound } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { api, ApiRequestError, type Me } from "../lib/api";
import { roleLabel } from "../lib/format";

export function ProfilePage() {
  const [me, setMe] = useState<Me | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api<Me>("/api/auth/me");
      setMe(response);
      setName(response.user.name);
      setPhone(response.user.phone ?? "");
      setEmail(response.user.email ?? "");
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat profil.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveProfile() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api("/api/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
        }),
      });
      setNotice("Profil diperbarui.");
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan profil.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    setError(null);
    setNotice(null);
    if (newPassword.length < 8) {
      setError("Password baru minimal 8 karakter.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Konfirmasi password baru belum sama.");
      return;
    }

    setChanging(true);
    try {
      await api("/api/auth/profile/password", {
        method: "POST",
        body: JSON.stringify({
          new_password: newPassword,
        }),
      });
      setNewPassword("");
      setConfirmPassword("");
      setNotice("Password berhasil diubah. Sesi lain sudah dicabut.");
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal mengubah password.",
      );
    } finally {
      setChanging(false);
    }
  }

  if (loading) {
    return (
      <div className="page-stack">
        <PageHeader
          eyebrow="Akun"
          title="Profil"
          description="Kelola identitas dan password akun Anda."
        />
        <div className="card card--pad">
          <span className="skeleton skeleton--title" />
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--block" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Akun"
        title="Profil"
        description="Ubah nama, kontak, dan password akun Anda sendiri."
      />

      {error ? (
        <div className="alert alert--danger" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      ) : null}

      <div className="settings-grid">
        <section className="card card--pad">
          <header className="report-card__head">
            <div>
              <p className="eyebrow">Identitas</p>
              <h2>Data akun</h2>
            </div>
            <UserRound size={20} />
          </header>

          <div className="drawer-meta settings-meta">
            <div>
              <span>Username</span>
              <strong>@{me?.user.username}</strong>
            </div>
            <div>
              <span>Role</span>
              <strong>{roleLabel(me?.membership.role ?? "")}</strong>
            </div>
            <div>
              <span>Tenant</span>
              <strong>{me?.tenant.name}</strong>
            </div>
            <div>
              <span>Status tenant</span>
              <strong>{me?.tenant.status}</strong>
            </div>
          </div>

          <div className="form-grid">
            <label className="field field--full">
              <span>Nama</span>
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength={2}
                maxLength={120}
              />
            </label>
            <label className="field">
              <span>Nomor WhatsApp</span>
              <input
                className="input"
                inputMode="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="08xxxxxxxxxx"
              />
            </label>
            <label className="field">
              <span>Email (opsional)</span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="nama@email.com"
              />
            </label>
          </div>
          <div className="card-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveProfile()}
              disabled={saving || name.trim().length < 2}
            >
              <Save size={16} /> {saving ? "Menyimpan..." : "Simpan profil"}
            </button>
          </div>
          <p className="fine-print">
            <Building2 size={14} /> Perubahan nama/tenant untuk akun owner
            dilakukan oleh owner sendiri atau superadmin platform.
          </p>
        </section>

        <section className="card card--pad">
          <header className="report-card__head">
            <div>
              <p className="eyebrow">Keamanan</p>
              <h2>Ganti password</h2>
            </div>
            <KeyRound size={20} />
          </header>

          <div className="form-grid">
            <label className="field">
              <span>Password baru</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={8}
              />
            </label>
            <label className="field">
              <span>Ulangi password baru</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
              />
            </label>
          </div>
          <div className="card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void changePassword()}
              disabled={
                changing ||
                newPassword.length < 8 ||
                newPassword !== confirmPassword
              }
            >
              <KeyRound size={16} />
              {changing ? "Mengubah..." : "Ubah password"}
            </button>
          </div>
          <p className="fine-print">
            Password staf juga dapat direset oleh owner dari menu Pengaturan.
          </p>
        </section>
      </div>
    </div>
  );
}
